import { useMemo } from 'react';

import LikertScale from '../LikertScale/LikertScale';

export interface RatingAnswer {
    value: number;
    count: number;
}

export interface RatingResultProps {
    /** Minimum rating value */
    from: number;
    /** Maximum rating value */
    to: number;
    /** Rating answers with counts */
    answers: RatingAnswer[];
    /** Optional start label */
    startLabel?: string;
    /** Optional end label */
    endLabel?: string;
    /** Optional class override */
    className?: string;
    /** Test id */
    dataTestId?: string;
}

/**
 * RatingResult
 *
 * Smart wrapper around LikertScale.
 * Converts raw rating counts into percentage-based data.
 */
const RatingResult = ({
    from,
    to,
    answers,
    startLabel = 'Strongly disagree',
    endLabel = 'Strongly agree',
    className,
    dataTestId = 'rating-result',
}: RatingResultProps) => {
    const data = useMemo<Record<number, number>>(() => {
        const total = answers.reduce((sum, item) => sum + item.count, 0);
        const map = new Map<number, number>();

        answers.forEach(({ value, count }) => {
            map.set(value, count);
        });

        const result: Record<number, number> = {};

        for (let i = from; i <= to; i++) {
            const count = map.get(i) ?? 0;
            result[i] = total > 0 ? Math.round((count / total) * 100) : 0;
        }

        return result;
    }, [answers, from, to]);

    return (
        <div className={`pt-6 pb-8 ${className ?? ''}`}>
            <LikertScale
                data={data}
                minValue={from}
                maxValue={to}
                startLabel={startLabel}
                endLabel={endLabel}
                dataTestId={dataTestId}
            />
        </div>
    );
};

export default RatingResult;
export { RatingResult };
